import {
  BODY,
  FIVE_STEP,
  LIVE_FAMILY_CAP,
  LIVE_STEPS,
  LIVE_VERDICTS,
  clusterById,
} from "@proef/domain";
import { z } from "zod";

/** One RTCIceServer as the browser takes it; TURN entries carry short-lived credentials. */
export const IceServer = z.object({
  urls: z.union([z.string().min(1), z.array(z.string().min(1)).min(1)]),
  username: z.string().exactOptional(),
  credential: z.string().exactOptional(),
});
export type IceServer = z.infer<typeof IceServer>;

/** GET /v1/rtc/ice -- the servers a peer connection may use, and how long to keep them. */
export const RtcIceResponse = z.object({
  iceServers: z.array(IceServer),
  ttlSeconds: z.number().int().positive(),
});
export type RtcIceResponse = z.infer<typeof RtcIceResponse>;

/** POST /v1/flights/:code/signal-ticket -- a single-use ticket that opens the flight's signalling
 * socket. A ticket rides in the socket URL because a browser WebSocket cannot send X-Device-Id. */
export const FlightSignalTicket = RtcIceResponse.extend({
  ticket: z.string().min(32).max(64),
});
export type FlightSignalTicket = z.infer<typeof FlightSignalTicket>;

/** The room assigns every socket a random peer id, so a frame can address a connection but
 * never a user or device. */
export const PeerId = z.uuid();

/** Session descriptions stay well under this; the cap keeps one frame from flooding a room. */
export const MAX_SDP_LENGTH = 32_000;

export const SignalPayload = z.discriminatedUnion("type", [
  z.object({ type: z.literal("offer"), sdp: z.string().max(MAX_SDP_LENGTH) }),
  z.object({ type: z.literal("answer"), sdp: z.string().max(MAX_SDP_LENGTH) }),
  z.object({
    type: z.literal("candidate"),
    candidate: z.string().max(2048),
    sdpMid: z.string().max(64).nullable(),
    sdpMLineIndex: z.number().int().min(0).max(255).nullable(),
  }),
]);
export type SignalPayload = z.infer<typeof SignalPayload>;

/** Client to room: relay `data` to one other peer in the same room. */
export const SignalOutgoing = z.object({
  kind: z.literal("signal"),
  to: PeerId,
  data: SignalPayload,
});
export type SignalOutgoing = z.infer<typeof SignalOutgoing>;

/** Client to room: everything a socket may send. `hello` opts the socket into the mesh and asks
 * who else is in it; a client that never says hello never receives offers. */
export const RoomClientFrame = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("hello") }),
  SignalOutgoing,
]);
export type RoomClientFrame = z.infer<typeof RoomClientFrame>;

/** Room to client: who is here on connect, relayed signals, and departures. The newcomer offers
 * to every peer in `peerIds`, so two peers never offer to each other at once. */
export const SignalEvent = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("peers"),
    self: PeerId,
    peerIds: z.array(PeerId),
    /** The account behind each peer, as the room (which authenticated the socket) knows it. A peer
     * cannot say who it is: absent from a flight's room, whose guests have no account. */
    users: z.record(PeerId, z.string().min(1).max(64)).exactOptional(),
  }),
  z.object({
    kind: z.literal("signal"),
    from: PeerId,
    /** The account behind `from`, set by the room for a newcomer's offer. */
    fromUser: z.string().min(1).max(64).exactOptional(),
    data: SignalPayload,
  }),
  z.object({ kind: z.literal("peer-left"), peerId: PeerId }),
]);
export type SignalEvent = z.infer<typeof SignalEvent>;

/** The most wines a flight holds, which bounds every wine index a peer can name. */
export const MAX_LIVE_WINE_INDEX = 23;

/**
 * What peers tell each other over the datachannel in a live session (docs/design/social-plan.md
 * 4.4). Every one is a hint: it makes a device refetch the session from the authenticated API, so a
 * hostile peer can cost an early refetch, never fake a submission or a reveal.
 */
const AromaFamily = z
  .string()
  .refine((id) => clusterById(id) !== undefined, { error: "not an aroma family" });

/**
 * What a co-taster has chosen so far on one wine: ids and scale values only, never free text or a
 * descriptor's words. Every field is a snapshot of the taster's current choice, so a later one
 * replaces the earlier and a missing field means nothing chosen.
 */
export const LivePicks = z.object({
  nose: z.array(AromaFamily).max(LIVE_FAMILY_CAP).exactOptional(),
  palate: z.array(AromaFamily).max(LIVE_FAMILY_CAP).exactOptional(),
  acidity: z.enum(FIVE_STEP).exactOptional(),
  tannin: z.enum(FIVE_STEP).exactOptional(),
  body: z.enum(BODY).exactOptional(),
  quality: z.enum(LIVE_VERDICTS).exactOptional(),
});
export type LivePicks = z.infer<typeof LivePicks>;

export const LiveEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("changed") }),
  z.object({
    type: z.literal("presence"),
    userId: z.string().min(1).max(64),
    state: z.enum(["joined", "left"]),
  }),
  z.object({
    type: z.literal("submitted"),
    userId: z.string().min(1).max(64),
    wineIdx: z.number().int().min(0).max(MAX_LIVE_WINE_INDEX),
  }),
  z.object({
    type: z.literal("reveal"),
    wineIdx: z.number().int().min(0).max(MAX_LIVE_WINE_INDEX),
  }),
  // Co-tasting. Neither says who: the receiver takes that from the room, which authenticated the
  // socket, so a peer can never speak as someone else. Neither changes anything at the edge, so
  // neither makes a device refetch.
  z.object({
    type: z.literal("step"),
    wineIdx: z.number().int().min(0).max(MAX_LIVE_WINE_INDEX),
    /** The guided page this person is on; null on a quick tasting, which has one page. */
    step: z.enum(LIVE_STEPS).nullable(),
    /** Whether this person shares their picks live; false hides them from the others' strips. */
    sharing: z.boolean(),
  }),
  z.object({
    type: z.literal("pick"),
    wineIdx: z.number().int().min(0).max(MAX_LIVE_WINE_INDEX),
    picks: LivePicks,
  }),
]);
export type LiveEvent = z.infer<typeof LiveEvent>;
